import { XIcon } from "@heroicons/react/solid";
import { useState, Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import Modal from "../../components/Modal";
import Button from "../../components/Button";
import { useEffect } from "react";
import { SegmentedControl, Slider } from "@mantine/core";

export const DefaultSettings = {
    textHeadingSize: 48,
    showPricePerAcre: false,
    completionsToShow: 10,
    transitionMode: "pagination",
    transitionDuration: 10,
    ppaThreshold: 30000,
    currentView: 1,
    tableTextSize: "lg", // Default text size
};

export default function GVSettingsModal({ open, onConfirm, onCancel }) {
    const getSettings = () => {
        const savedData = localStorage.getItem("gvsettings");
        return {
            ...DefaultSettings,
            ...(savedData && JSON.parse(savedData)),
        };
    };
    const [settings, setSettings] = useState(getSettings);

    useEffect(() => {
        if (open) {
            setSettings(getSettings);
        }
    }, [open]);

    const handleConfirm = (e) => {
        e.preventDefault();
        localStorage.setItem("gvsettings", JSON.stringify(settings));
        onConfirm();
    };

    return (
        <Modal isOpen={open}>
            <div className="min-h-screen px-4 text-center">
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <Dialog.Overlay className="fixed inset-0" />
                </Transition.Child>

                {/* This element is to trick the browser into centering the modal contents. */}
                <span
                    className="inline-block h-screen align-middle"
                    aria-hidden="true"
                >
                    &#8203;
                </span>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0 scale-95"
                    enterTo="opacity-100 scale-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100 scale-100"
                    leaveTo="opacity-0 scale-95"
                >
                    <form
                        onSubmit={handleConfirm}
                        className="inline-block w-full max-w-xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl"
                    >
                        <XIcon
                            className="float-right w-6 h-6 ml-1 cursor-pointer"
                            onClick={onCancel}
                        />

                        <Dialog.Title
                            as="h3"
                            className="text-lg font-medium leading-6 text-gray-900"
                        >
                            Edit Settings
                        </Dialog.Title>
                        <hr className="my-4" />

                        <div className="flex items-center gap-4 my-4">
                            <div>
                                <label for="settings-option-1">
                                    Table Text Size
                                </label>
                            </div>
                            <div className="flex-1">
                                <div className="px-2 py-3">
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-xs">A</span>
                                        <span className="text-xl">A</span>
                                    </div>
                                    <Slider
                                        value={
                                            settings.tableTextSize === "lg"
                                                ? 0
                                                : settings.tableTextSize ===
                                                  "xl"
                                                ? 1
                                                : settings.tableTextSize ===
                                                  "2xl"
                                                ? 2
                                                : settings.tableTextSize ===
                                                  "3xl"
                                                ? 3
                                                : settings.tableTextSize ===
                                                  "4xl"
                                                ? 4
                                                : 0
                                        }
                                        onChange={(value) => {
                                            const sizeMap = [
                                                "lg",
                                                "xl",
                                                "2xl",
                                                "3xl",
                                                "4xl",
                                            ];
                                            setSettings({
                                                ...settings,
                                                tableTextSize: sizeMap[value],
                                            });
                                        }}
                                        min={0}
                                        max={4}
                                        step={1}
                                        marks={[
                                            { value: 0, label: "L" },
                                            { value: 1, label: "XL" },
                                            { value: 2, label: "2XL" },
                                            { value: 3, label: "3XL" },
                                            { value: 4, label: "4XL" },
                                        ]}
                                        styles={{
                                            track: {
                                                backgroundColor:
                                                    "#AD272C !important",
                                                fill: "#AD272C",
                                                color: "#AD272C",
                                            },
                                            bar: {
                                                backgroundColor: "#AD272C",
                                            },
                                            thumb: {
                                                backgroundColor: "#ffffff",
                                                borderColor: "#AD272C",
                                                color: "#AD272C",
                                            },
                                            mark: {
                                                color: "#AD272C",
                                                borderColor: "#AD272C",
                                                borderLeft: "1px solid #fff",
                                                borderRight: "1px solid #fff",
                                                borderTop: "1px solid #fff",
                                                borderBottom: "1px solid #fff",
                                            },
                                            markLabel: { fontSize: "0.7rem" },
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-4 my-4">
                            <div>
                                <label for="settings-option-0">
                                    Total Text Size
                                </label>
                            </div>
                            <div className="flex-1">
                                <input
                                    id="settings-option-0"
                                    name="textHeadingSize"
                                    type="number"
                                    className="border-gray-300 cursor-pointer focus:ring-cta text-cta focus:outline-none outline-none"
                                    value={settings.textHeadingSize}
                                    onChange={(e) =>
                                        setSettings({
                                            ...settings,
                                            textHeadingSize: parseInt(
                                                e.target.value
                                            ),
                                        })
                                    }
                                />
                            </div>
                        </div>
                        <div className="flex items-center gap-4 my-4">
                            <div>
                                <label for="settings-option-2">
                                    Completion Count
                                </label>
                            </div>
                            <div className="flex-1">
                                <select
                                    id="settings-option-2"
                                    name="completion_count_setting"
                                    className="border-gray-300 cursor-pointer focus:ring-cta text-cta focus:outline-none outline-none"
                                    value={settings.completionsToShow + ""}
                                    onChange={(e) =>
                                        setSettings({
                                            ...settings,
                                            completionsToShow: parseInt(
                                                e.target.value
                                            ),
                                        })
                                    }
                                >
                                    <option>1</option>
                                    <option>2</option>
                                    <option>3</option>
                                    <option>4</option>
                                    <option>5</option>
                                    <option>6</option>
                                    <option>7</option>
                                    <option>8</option>
                                    <option>9</option>
                                    <option>10</option>
                                </select>
                            </div>
                        </div>

                        <div className="flex items-center gap-4 my-4">
                            <div>
                                <label for="settings-option-3">
                                    PPA Threshold
                                </label>
                            </div>
                            <div className="flex-1">
                                <input
                                    id="settings-option-3"
                                    name="ppaThreshold"
                                    type="number"
                                    className="border-gray-300 cursor-pointer focus:ring-cta text-cta focus:outline-none outline-none"
                                    value={settings.ppaThreshold}
                                    onChange={(e) =>
                                        setSettings({
                                            ...settings,
                                            ppaThreshold: parseInt(
                                                e.target.value
                                            ),
                                        })
                                    }
                                />
                            </div>
                        </div>

                        <div className="flex items-center gap-4 my-4">
                            <div>
                                <label for="settings-option-0">
                                    Show Price Per Acre (PPA)
                                </label>
                            </div>
                            <div className="flex-1">
                                <input
                                    id="settings-option-0"
                                    name="privacy_setting"
                                    type="checkbox"
                                    className="w-4 h-4 border-gray-300 cursor-pointer focus:ring-indigo-500 text-cta"
                                    checked={settings.showPricePerAcre}
                                    onChange={(e) =>
                                        setSettings({
                                            ...settings,
                                            showPricePerAcre: e.target.checked,
                                        })
                                    }
                                />
                            </div>
                        </div>
                        {/* <div className="flex items-center gap-4 my-4">
                            <div>
                                change settings number
                                <label for="settings-option-2">
                                    Bid Transition Type
                                </label>
                            </div>
                            <div className="flex-1">
                                <select
                                    id="settings-option-2"
                                    name="transitionMode"
                                    className="border-gray-300 cursor-pointer focus:ring-cta text-cta focus:outline-none outline-none"
                                    value={settings.transitionMode + ""}
                                    onChange={(e) =>
                                        setSettings({
                                            ...settings,
                                            transitionMode: e.target.value,
                                        })
                                    }
                                >
                                    <option value="pagination">Pagination</option>
                                    <option value="scroll">Scroll</option>
                                </select>
                            </div>
                        </div> */}

                        <div className="flex items-center gap-4 my-4">
                            <div>
                                <label for="settings-option-3">
                                    Transition Duration (seconds)
                                </label>
                            </div>
                            <div className="flex-1">
                                <input
                                    id="settings-option-3"
                                    name="transitionDuration"
                                    type="number"
                                    min={5}
                                    className="border-gray-300 cursor-pointer focus:ring-cta text-cta focus:outline-none outline-none"
                                    value={settings.transitionDuration}
                                    onChange={(e) =>
                                        setSettings({
                                            ...settings,
                                            transitionDuration: parseInt(
                                                e.target.value
                                            ),
                                        })
                                    }
                                />
                            </div>
                        </div>

                        <div className="flex items-center gap-4 my-4">
                            <div>
                                <label for="settings-option-4">
                                    Change View
                                </label>
                            </div>
                            <div className="flex-1">
                                <SegmentedControl
                                    fullWidth
                                    size="md"
                                    value={settings.currentView}
                                    onChange={(value) =>
                                        setSettings({
                                            ...settings,
                                            currentView: parseInt(value),
                                        })
                                    }
                                    data={[
                                        {
                                            label: "Table",
                                            value: 1,
                                        },
                                        {
                                            label: "Map",
                                            value: 2,
                                        },
                                        {
                                            label: "Hybrid",
                                            value: 3,
                                        },
                                    ]}
                                    styles={{
                                        indicator: {
                                            backgroundColor: "white",
                                            boxShadow:
                                                "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
                                        },
                                        label: {
                                            "&[data-active]": {
                                                backgroundColor: "white",
                                            },
                                        },
                                    }}
                                />
                            </div>
                        </div>

                        <div className="flex items-center mt-10 text-cta">
                            <div className="w-1/2 text-left item-center">
                                <Button
                                    type={"secondary"}
                                    onClick={() => onCancel()}
                                    rounded
                                >
                                    Cancel
                                </Button>
                            </div>
                            <div className="w-1/2 text-right">
                                <Button htmlType="submit" rounded>
                                    Save
                                </Button>
                            </div>
                        </div>
                    </form>
                </Transition.Child>
            </div>
        </Modal>
    );
}
